import { type ChangeEvent } from "react";
import {
  Text,
  Pagination,
  Loader,
  Box,
  Select,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrlTeamMembers } from "@fwa/src/services/teamService";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { queryAsString } from "@fwa/src/utils/urlUtils";

import { MemberFundraisingPageDonate } from "@fwa/src/components/MemberFundraisingPageDonate";
import { MemberFundraisingPageEdit } from "@fwa/src/components/MemberFundraisingPageEdit";
import { MemberFundraisingPageSelectLeader } from "@fwa/src/components/MemberFundraisingPageSelectLeader";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type {
  TeamMemberType,
  TeamMemberSortType,
  FwsResponseData,
} from "@fwa/src/types";

export const fundraisingPageListItemsTypes = {
  donate: "donate",
  edit: "edit",
  selectLeader: "selectLeader",
} as const;

type FundraisingPageListItemType =
  (typeof fundraisingPageListItemsTypes)[keyof typeof fundraisingPageListItemsTypes];

export function TeamMembers({
  teamId,
  itemsPerPage,
  listItemType = fundraisingPageListItemsTypes.donate,
}: {
  teamId: string;
  itemsPerPage: number;
  listItemType: FundraisingPageListItemType;
}) {
  const router = useRouter();
  const { trackError } = useTracking();
  const { members_page: membersPage, sort: sortQuery } = router?.query || {};
  const sort = (queryAsString(sortQuery) || "leader") as TeamMemberSortType;
  const currentPage = parseInt(queryAsString(membersPage) || "1", 10);
  const fetchUrl = teamId
    ? fwsUrlTeamMembers({
        teamId,
        currentPage,
        itemsPerPage,
        sort,
      })
    : null;

  const { data, error, totalItems } = useSwrGet<
    FwsResponseData<TeamMemberType>
  >(fetchUrl, {
    suspense: false,
  });
  const memberships = data?.results || [];

  // We are effectively storing page number in the query string
  const setPageInQueryString = (pageNumber: number) => {
    // pathname isn't going to cut the mustard because it's on the teams page with a [slug]
    // so we need to make the url look like how it should do which is annoying because this list should be
    // agnostic to the page path however it still needs to know how to amend the query string in the URL
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, members_page: pageNumber },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "TeamMembers",
          function: "setPageInQueryString",
        });
      });
  };

  const setSortInQueryString = (sortValue: string) => {
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, sort: sortValue },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "TeamMembers",
          function: "setSortInQueryString",
        });
      });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const sortValue = e.target.value as TeamMemberSortType;
    setSortInQueryString(sortValue || null);
  };

  // Superteams don't have team leaders or teamLeaderPages only Teams do
  const isLeaderForMembership = (member: TeamMemberType): boolean =>
    "teamLeaderPage" in member.parentPage &&
    !!member?.childPage?.uniqueId &&
    member?.parentPage?.teamLeaderPage?.uniqueId ===
      member?.childPage?.uniqueId;

  const firstItemNumber = Math.max(currentPage - 1, 0) * itemsPerPage + 1;

  return (
    <>
      {/* Error State */}
      {error ? <Text as="span">Unable to get team members</Text> : null}

      {/* Loading State */}
      {!error && !data ? <Loader /> : null}

      {/* Empty State */}
      {!error && data && !memberships.length ? (
        <Text as="span">There are currently no team members</Text>
      ) : null}

      {/* Data State */}
      {memberships.length ? (
        <>
          <Box>
            <div className={sharedStyles["filter-wrapper"]}>
              <Select
                required
                value={sort}
                label="Sort members by"
                onChange={handleFilterChange}
                aria-controls="memberList"
                hideRequiredIndicationInLabel
              >
                <option value="alpha">A-Z</option>
                <option value="amount">amount raised</option>
                <option value="recent">recently joined</option>
                <option value="leader">leader first</option>
              </Select>
            </div>
          </Box>
          <Box>
            <div className={sharedStyles["row-space-around"]}>
              <Text>{`Showing ${firstItemNumber} to ${
                firstItemNumber + memberships.length - 1
              } of ${totalItems} members`}</Text>
            </div>
          </Box>
          <Box>
            <ul
              className={sharedStyles["member-list"]}
              id="memberList"
              aria-live="assertive"
            >
              {!error &&
                memberships
                  .filter((member: TeamMemberType) => member.uniqueId)
                  .map((member: TeamMemberType) => (
                    <li key={member.uniqueId}>
                      {listItemType === fundraisingPageListItemsTypes.donate ? (
                        <MemberFundraisingPageDonate
                          fundraisingPage={member.childPage}
                        />
                      ) : null}
                      {listItemType === fundraisingPageListItemsTypes.edit ? (
                        <MemberFundraisingPageEdit
                          fundraisingPage={member.childPage}
                          isLeader={isLeaderForMembership(member)}
                          teamId={teamId}
                        />
                      ) : null}
                      {listItemType ===
                      fundraisingPageListItemsTypes.selectLeader ? (
                        <MemberFundraisingPageSelectLeader
                          fundraisingPage={member.childPage}
                          isLeader={isLeaderForMembership(member)}
                          teamId={teamId}
                        />
                      ) : null}
                    </li>
                  ))}
            </ul>
          </Box>
          <Pagination
            aria-controls="memberList"
            current={currentPage}
            perPage={itemsPerPage}
            items={totalItems}
            hideLast={false}
            pagerCallback={(n: number) => {
              setPageInQueryString(n);
            }}
          />
        </>
      ) : null}
    </>
  );
}

export default TeamMembers;
